import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { animate, motion } from 'motion/react';
import { useEffect, useState } from 'react';

// A yoyo loop.
const YOYO = {
  repeat: Number.POSITIVE_INFINITY,
  repeatType: 'reverse',
  ease: 'easeInOut',
} as const;

// `scale` on an SVG child is relative to the SVG user-space origin unless the transform box
// is the element's own bounding box.
const SCALE_AROUND_SELF = { transformBox: 'fill-box', transformOrigin: '50% 50%' } as const;

export function DistanceFromTrack() {
  const [value, setValue] = useState(30);

  useEffect(() => {
    const controls = animate(30, 60, {
      duration: 3.0,
      ...YOYO,
      onUpdate: (v) => setValue(Math.round(v)),
    });
    return () => controls.stop();
  }, []);

  return (
    <Border>
      <svg width="100%" height="100%" viewBox="0 0 320 200" fill="none" stroke="black">
        <title>DistanceFromTrack Animation</title>

        {/* Legend */}
        <circle cx="16" cy="16" r="5" fill={RacemapColors.DarkGray} />
        <text x="26" y="20" fontSize="12" fill="black" stroke="none">
          Waypoint
        </text>
        <circle cx="16" cy="36" r="5" fill={RacemapColors.LightGreen} />
        <text x="26" y="40" fontSize="12" fill="black" stroke="none">
          Reader next to the track
        </text>
        <circle cx="16" cy="56" r="5" fill="red" />
        <text x="26" y="60" fontSize="12" fill="black" stroke="none">
          Reader too far away from the track
        </text>

        <g transform="translate(0, 14)">
          <motion.path
            d="M -10 110 L 40 130 L 100 90 L 140 130 L 240 130 L 300 80 L 340 90"
            stroke={RacemapColors.PaleBlue}
            radius={10}
            initial={{ strokeWidth: 20 }}
            animate={{ strokeWidth: 40 }}
            transition={{ duration: 3.0, ...YOYO }}
            strokeLinecap="square"
            strokeLinejoin="round"
            opacity={0.3}
            fill="none"
          />

          <path
            d="M -10 110 L 40 130 L 100 90 L 140 130 L 240 130 L 300 80 L 340 90"
            stroke="black"
            strokeWidth="2"
            strokeLinecap="square"
            fill="none"
          />

          <circle cx="40" cy="130" r="5" fill={RacemapColors.DarkGray} />
          <circle cx="100" cy="90" r="5" fill={RacemapColors.DarkGray} />
          <circle cx="140" cy="130" r="5" fill={RacemapColors.DarkGray} />
          <circle cx="240" cy="130" r="5" fill={RacemapColors.DarkGray} />
          <circle cx="300" cy="80" r="5" fill={RacemapColors.DarkGray} />

          <path
            d="M160 160 L 160 90 L 170 80 L 196 80 "
            stroke={RacemapColors.DarkGray}
            strokeWidth="1"
            fill="none"
          />

          <path
            d="M160 130 L 157 142 L 163 142 "
            stroke="none"
            strokeWidth="1"
            fill={RacemapColors.DarkGray}
          />
          <path
            d="M160 110 L 157 98 L 163 98 "
            stroke="none"
            strokeWidth="1"
            fill={RacemapColors.DarkGray}
          />

          {/* Reader next to te track */}
          <circle id="Reader_A" cx="80" cy="94" r="5" fill={RacemapColors.LightGreen} />
          <motion.circle
            id="Reader_B"
            cx="256"
            cy="94"
            r="5"
            initial={{ fill: RacemapColors.BaseRed }}
            animate={{ fill: RacemapColors.LightGreen }}
            transition={{ delay: 1.0, duration: 0.5, repeatDelay: 2.5, ...YOYO }}
          />

          <text x="184" y="76" textAnchor="middle" fontSize="20" fill="black" stroke="none">
            {value}
          </text>
        </g>
      </svg>
    </Border>
  );
}

export function RSSIThresehold() {
  const P1 = { x: 46, y: 158 };
  const P2 = { x: 72, y: 115 };
  const P3 = { x: 120, y: 85 };
  const P4 = { x: 190, y: 69 };
  const P5 = { x: 272, y: 64 };
  const P6 = { x: 100, y: 180 };

  return (
    <Border>
      <svg width="100%" height="100%" viewBox="0 0 320 200" fill="none" stroke="black">
        <title>JumpingDotThresehold Animation</title>

        {/* reader range */}
        <circle cx={P6.x} cy={P6.y} r="5" fill={RacemapColors.LightGreen} />
        <circle cx={P6.x} cy={P6.y} r="30" stroke={RacemapColors.LightLightGray} />
        <circle cx={P6.x} cy={P6.y} r="50" stroke={RacemapColors.LightLightGray} />
        <circle cx={P6.x} cy={P6.y} r="72" stroke={RacemapColors.LightLightGray} />
        <circle cx={P6.x} cy={P6.y} r="96" stroke={RacemapColors.LightLightGray} />
        <circle cx={P6.x} cy={P6.y} r="122" stroke={RacemapColors.LightGray} />
        <g transform="rotate(45, 190, 50)">
          <text
            x={P6.x + 100}
            y={P6.y - 100}
            fontSize="8"
            fill={RacemapColors.LightGray}
            stroke="none"
          >
            -75dBm
          </text>
        </g>
        <circle cx={P6.x} cy={P6.y} r="150" stroke={RacemapColors.LightLightGray} />
        <circle cx={P6.x} cy={P6.y} r="180" stroke={RacemapColors.LightLightGray} />
        <circle cx={P6.x} cy={P6.y} r="212" stroke={RacemapColors.LightLightGray} />
        <circle cx={P6.x} cy={P6.y} r="246" stroke={RacemapColors.LightLightGray} />

        {/* Legend */}
        <circle cx="16" cy="16" r="5" fill={RacemapColors.LightGreen} />
        <text x="26" y="20" fontSize="12" fill="black" stroke="none">
          Reader
        </text>
        <circle cx="16" cy="36" r="5" fill={RacemapColors.PaleBlue} />
        <text x="26" y="40" fontSize="12" fill="black" stroke="none">
          Transponder in Range
        </text>
        <circle cx="16" cy="56" r="5" fill={RacemapColors.BaseRed} />
        <text x="26" y="60" fontSize="12" fill="black" stroke="none">
          Transponder out of Range
        </text>

        {/* Transponders */}
        <circle cx={P1.x} cy={P1.y} r="5" fill={RacemapColors.PaleBlue} />
        <circle cx={P2.x} cy={P2.y} r="5" fill={RacemapColors.PaleBlue} />
        <circle cx={P3.x} cy={P3.y} r="5" fill={RacemapColors.PaleBlue} />
        <circle cx={P4.x} cy={P4.y} r="5" fill={RacemapColors.BaseRed} />
        <circle cx={P5.x} cy={P5.y} r="5" fill={RacemapColors.BaseRed} />
      </svg>
    </Border>
  );
}

// The 6 track definition points. The dots walk this polyline — which is what the drawn
// <polyline> below shows. GSAP's MotionPathPlugin curve-fitted a spline through these, so the
// dots used to drift slightly off the visible track; a keyframe walk sits exactly on it.
const P1 = { x: 20, y: 130 };
const P2 = { x: 80, y: 95 };
const P3 = { x: 130, y: 75 };
const P4 = { x: 190, y: 110 };
const P5 = { x: 270, y: 140 };
const P6 = { x: 330, y: 130 };

const LOOP_DURATION = 8;

// Keyframe times proportional to segment length, so the dot moves at a constant speed across
// segments of unequal length (what GSAP's `ease: 'linear'` along a motion path gave us).
function walk(points: Array<{ x: number; y: number }>) {
  const lengths = points.slice(1).map((p, i) => Math.hypot(p.x - points[i].x, p.y - points[i].y));
  const total = lengths.reduce((sum, l) => sum + l, 0);
  const times = [0];
  for (const length of lengths) {
    times.push(times[times.length - 1] + length / total);
  }
  return {
    cx: points.map((p) => p.x),
    cy: points.map((p) => p.y),
    times,
  };
}

const offsetY = (points: Array<{ x: number; y: number }>, dy: number) =>
  points.map((p) => ({ x: p.x, y: p.y + dy }));

const REAL_WALK = walk([P1, P2, P3, P4, P5]);
const VIRTUAL_WALK = walk(offsetY([P2, P3, P4, P5, P6], -2));
const JUMPED_WALK = walk(offsetY([P1, P2, P3, P4, P5], -2));

// The readouts the original latched in five useStates and reset on every loop. Expressed as a
// function of loop time they reset for free — the held values are just the window end values.
function readoutsAt(t: number) {
  return {
    reader3DeltaT: t <= 3.2 ? 0 : t < 3.5 ? 100 * (t - 3.2) : 30,
    reader4DeltaT: t <= 4.6 ? 0 : t < 5.4 ? 100 * (t - 4.6) : 80,
    reader4TextColor: t > 5.0 ? 'red' : 'black',
    virtStarterVisible: t > 2.0 && t <= 5.7 ? 'visible' : 'hidden',
    jumpStarterVisible: t > 5.7 ? 'visible' : 'hidden',
  };
}

type Readouts = ReturnType<typeof readoutsAt>;

// Readouts are constant for ~6.9s of every 8s loop. Reusing the previous object then lets
// React skip the render instead of re-rendering every frame.
const sameReadouts = (a: Readouts, b: Readouts) =>
  a.reader3DeltaT === b.reader3DeltaT &&
  a.reader4DeltaT === b.reader4DeltaT &&
  a.reader4TextColor === b.reader4TextColor &&
  a.virtStarterVisible === b.virtStarterVisible &&
  a.jumpStarterVisible === b.jumpStarterVisible;

export function JumpingDotThresehold() {
  // Everything the readouts show is a function of where we are in the 8s loop, so derive it
  // rather than latching it in five separate states with a reset on every repeat.
  const [readouts, setReadouts] = useState(() => readoutsAt(0));

  useEffect(() => {
    const controls = animate(0, LOOP_DURATION, {
      duration: LOOP_DURATION,
      ease: 'linear',
      repeat: Number.POSITIVE_INFINITY,
      onUpdate: (t) =>
        setReadouts((prev) => {
          const next = readoutsAt(t);
          return sameReadouts(prev, next) ? prev : next;
        }),
    });
    return () => controls.stop();
  }, []);

  const { reader3DeltaT, reader4DeltaT, reader4TextColor, virtStarterVisible, jumpStarterVisible } =
    readouts;

  return (
    <Border>
      <svg width="100%" height="100%" viewBox="0 0 320 200" fill="none" stroke="black">
        <title>JumpingDotThresehold Animation</title>
        {/* The track consisting of 6 definition points */}
        <polyline
          points={`${P1.x},${P1.y} ${P2.x},${P2.y} ${P3.x},${P3.y} ${P4.x},${P4.y} ${P5.x},${P5.y} ${P6.x},${P6.y}`}
          stroke="black"
          fill="none"
          strokeWidth="2"
        />

        {/* Legend */}
        <circle cx="16" cy="16" r="5" fill={RacemapColors.LightGreen} />
        <text x="26" y="20" fontSize="12" fill="black" stroke="none">
          Reader
        </text>
        <circle cx="16" cy="36" r="5" fill={RacemapColors.BaseRed} />
        <text x="26" y="40" fontSize="12" fill="black" stroke="none">
          Real world starter
        </text>
        <circle cx="16" cy="56" r="5" fill={RacemapColors.PaleBlue} />
        <text x="26" y="60" fontSize="12" fill="black" stroke="none">
          Jumping virtual starter
        </text>
        <text
          visibility={jumpStarterVisible}
          x="240"
          y="100"
          fontSize="12"
          fill="black"
          stroke="none"
        >
          Jump !!!
        </text>

        {/* Readers */}
        <circle cx={P1.x} cy={P1.y} r="5" fill={RacemapColors.LightGreen} />
        <circle cx={P2.x} cy={P2.y} r="5" fill={RacemapColors.LightGreen} />
        <circle cx={P3.x} cy={P3.y} r="5" fill={RacemapColors.LightGreen} />
        <circle cx={P4.x} cy={P4.y} r="5" fill={RacemapColors.LightGreen} />
        <circle cx={P5.x} cy={P5.y} r="5" fill={RacemapColors.LightGreen} />
        <circle cx={P6.x} cy={P6.y} r="5" fill={RacemapColors.LightGreen} />

        <RadiatingCircles point={P1} idBase="P1" delayOffset={0} />
        <RadiatingCircles point={P2} idBase="P2" delayOffset={1.5} />
        <RadiatingCircles point={P3} idBase="P3" delayOffset={3.5} />
        <RadiatingCircles point={P4} idBase="P4" delayOffset={5.5} />

        {/* Moving circles */}
        <motion.circle
          cx={P1.x}
          cy={P1.y}
          r="5"
          fill={RacemapColors.BaseRed}
          animate={{ cx: REAL_WALK.cx, cy: REAL_WALK.cy }}
          transition={{
            duration: LOOP_DURATION,
            ease: 'linear',
            repeat: Number.POSITIVE_INFINITY,
            times: REAL_WALK.times,
          }}
        />
        <motion.circle
          visibility={virtStarterVisible}
          cx={P2.x}
          cy={P2.y - 2}
          r="5"
          fill={RacemapColors.PaleBlue}
          animate={{ cx: VIRTUAL_WALK.cx, cy: VIRTUAL_WALK.cy }}
          transition={{
            duration: 6,
            delay: 2,
            repeatDelay: 2,
            ease: 'linear',
            repeat: Number.POSITIVE_INFINITY,
            times: VIRTUAL_WALK.times,
          }}
        />
        <motion.circle
          visibility={jumpStarterVisible}
          cx={P1.x}
          cy={P1.y - 2}
          r="5"
          fill={RacemapColors.PaleBlue}
          animate={{ cx: JUMPED_WALK.cx, cy: JUMPED_WALK.cy }}
          transition={{
            duration: LOOP_DURATION,
            ease: 'linear',
            repeat: Number.POSITIVE_INFINITY,
            times: JUMPED_WALK.times,
          }}
        />

        {/* DeltaT measurements */}
        <g>
          <path d="M 111 118 L 129 82" stroke="grey" strokeWidth="1" />
          <rect x="60" y="120" width="50" height="20" fill="none" stroke="black" />
          <text x="63" y="134" fontSize="12" fill="black" stroke="none">
            {`Δt ${reader3DeltaT.toFixed(1)}s`}
          </text>
        </g>

        <g>
          <path d="M 192 103 L 209 72" stroke="grey" strokeWidth="1" />
          <rect x="210" y="50" width="50" height="20" fill="none" stroke="black" />
          <text x="213" y="64" fontSize="12" fill={reader4TextColor} stroke="none">
            {`Δt ${reader4DeltaT.toFixed(1)}s`}
          </text>
        </g>
      </svg>
    </Border>
  );
}

// Three concentric "radio wave" rings expanding out of a reader, staggered half a second
// apart. `delayOffset` shifts the whole station so the four readers fire in sequence.
function RadiatingCircles({
  point,
  idBase,
  delayOffset,
}: {
  point: { x: number; y: number };
  idBase: string;
  delayOffset: number;
}) {
  return (
    <g>
      {[0, 1, 2].map((index) => (
        <motion.circle
          key={`${idBase}_${index}`}
          cx={point.x}
          cy={point.y}
          r="5"
          fill="none"
          stroke={RacemapColors.PaleBlue}
          strokeWidth="1.0"
          strokeDasharray="8"
          style={SCALE_AROUND_SELF}
          initial={{ scale: 1, opacity: 1 }}
          animate={{ scale: 5, opacity: 0 }}
          transition={{
            duration: 1.5,
            ease: 'linear',
            repeat: Number.POSITIVE_INFINITY,
            repeatDelay: 6.5,
            delay: index * 0.5 + delayOffset,
          }}
        />
      ))}
    </g>
  );
}

const Border = styled.div`
  border: 1px solid #ccc;
  margin-bottom: 10px;
`;
